import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { PipelineStatsBar } from "./components/PipelineStatsBar";
import { PipelineKanbanBoard, type StageConfigItem } from "./components/PipelineKanbanBoard";
import { PipelineTableView } from "./components/PipelineTableView";
import { PipelineForecastView } from "./components/PipelineForecastView";
import AddDealModal, { type PipelineDeal } from "./components/AddDealModal";
import { useToast } from "@/components/base/Toast";
import { usePipelineActivities } from "@/components/feature/PipelineActivityContext";
import { exportToCSV, formatExportDate } from "@/utils/exportUtils";
import type { Lead, Priority } from "@/mocks/leadsData";
import { fetchApi } from "@/services/apiService";

export type DealMetaMap = Record<
  number,
  { leadId: number; dealValue: string; dealPriority: Priority; dealAssigned: string; dealCreatedAt: string }
>;

type PipelineData = Record<string, number[]>;

const defaultStageConfig: StageConfigItem[] = [
  { stage: "New Inquiry", label: "New Inquiry", colorBar: "bg-gray-400" },
  { stage: "Site Visit Scheduled", label: "Site Visit Scheduled", colorBar: "bg-blue-500" },
  { stage: "Site Visit Done", label: "Site Visit Done", colorBar: "bg-indigo-500" },
  { stage: "Proposal Sent", label: "Proposal Sent", colorBar: "bg-amber-500" },
  { stage: "Negotiation", label: "Negotiation", colorBar: "bg-orange-500" },
  { stage: "Booking Done", label: "Booking Done", colorBar: "bg-green-500" },
  { stage: "Lost", label: "Lost", colorBar: "bg-red-400" },
];

export default function PipelinePage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"kanban" | "table" | "forecast">("kanban");
  const { showToast } = useToast();
  const { logStageChange } = usePipelineActivities();
  const [pipelineData, setPipelineData] = useState<PipelineData>({});
  const [dealMetaMap] = useState<DealMetaMap>({});
  const [addDealModalOpen, setAddDealModalOpen] = useState(false);
  const [addDealPreSelectedStage, setAddDealPreSelectedStage] = useState<string | undefined>(undefined);
  const [exportOpen, setExportOpen] = useState(false);
  const [dynamicLeads, setDynamicLeads] = useState<Lead[]>([]);
  const [stageConfig, setStageConfig] = useState<StageConfigItem[]>(defaultStageConfig);
  const [stageDefinitions, setStageDefinitions] = useState<any[]>([]);
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const exportRef = useRef<HTMLDivElement>(null);

  // ── 1. Fetch dynamic pipeline stages ────────────────────────
  const loadPipelineStages = useCallback(async () => {
    try {
      const res = await fetchApi<any[]>("/config/pipeline-stages/get-all", undefined, []);
      const items = Array.isArray(res) ? res : (res as any)?.data || [];
      if (items.length > 0) {
        setStageDefinitions(items);
        const mapped: StageConfigItem[] = items.map((s: any) => ({
          stage: s.name,
          label: s.name,
          colorBar: s.color || "bg-indigo-500",
        }));
        setStageConfig(mapped);
        return mapped;
      }
    } catch (err) {
      console.warn("Could not load dynamic pipeline stages, using defaults.", err);
    }
    return defaultStageConfig;
  }, []);

  // ── 2. Fetch dynamic leads & construct pipeline groupings ───
  const loadPipelineData = useCallback(async () => {
    setIsLoading(true);
    try {
      const currentStages = await loadPipelineStages();
      const res = await fetchApi<{ items?: Lead[]; total?: number }>("/leads?limit=100", undefined, { items: [] });
      const items = res?.items || (Array.isArray(res) ? (res as any) : []);

      if (Array.isArray(items) && items.length > 0) {
        setDynamicLeads(items);

        // Group dynamically by active stage list
        const grouped: PipelineData = {};
        currentStages.forEach((s) => {
          grouped[s.stage] = [];
        });

        const fallbackStage = currentStages[0]?.stage || "New Inquiry";
        if (!grouped[fallbackStage]) grouped[fallbackStage] = [];

        items.forEach((l) => {
          const leadStage = l.stage || fallbackStage;
          if (grouped[leadStage]) {
            grouped[leadStage].push(l.id);
          } else {
            // If stage exists in lead but not in predefined buckets, create bucket or put in fallback
            grouped[leadStage] = [l.id];
          }
        });

        setPipelineData(grouped);
      } else {
        setDynamicLeads([]);
        const grouped: PipelineData = {};
        currentStages.forEach((s) => {
          grouped[s.stage] = [];
        });
        setPipelineData(grouped);
      }
    } catch (err) {
      console.error("Error loading pipeline data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [loadPipelineStages]);

  useEffect(() => {
    loadPipelineData();
    // Fetch dynamic projects
    fetchApi<any[]>("/projects", undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        setProjectsList(items);
      })
      .catch(() => {});
  }, [loadPipelineData]);

  // Close export dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    if (exportOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [exportOpen]);

  const allDeals = useMemo(() => {
    if (selectedProjectFilter === "all") return dynamicLeads;
    return dynamicLeads.filter(
      (l) =>
        String(l.projectId) === String(selectedProjectFilter) ||
        String(l.project_id) === String(selectedProjectFilter) ||
        l.projectName === selectedProjectFilter
    );
  }, [dynamicLeads, selectedProjectFilter]);

  const filteredPipelineData = useMemo(() => {
    const grouped: PipelineData = {};
    stageConfig.forEach((s) => {
      grouped[s.stage] = [];
    });
    const fallbackStage = stageConfig[0]?.stage || "New Inquiry";
    if (!grouped[fallbackStage]) grouped[fallbackStage] = [];

    allDeals.forEach((l) => {
      const leadStage = l.stage || fallbackStage;
      if (grouped[leadStage]) {
        grouped[leadStage].push(l.id);
      } else {
        grouped[leadStage] = [l.id];
      }
    });
    return grouped;
  }, [allDeals, stageConfig]);

  const existingDealLeadIds = allDeals.map((d) => d.id);

  // ── Handle stage change from Table View or child views ─────
  const handleTableStageChange = useCallback(
    async (dealId: number, newStage: string) => {
      const lead = allDeals.find((l) => l.id === dealId);
      const fromStage = lead?.stage || "New Inquiry";

      try {
        await fetchApi(`/leads/${dealId}`, {
          method: "PATCH",
          body: JSON.stringify({ stage: newStage }),
        });

        showToast(`${lead?.name || "Deal"} moved to ${newStage}`, "success");

        if (lead && fromStage !== newStage) {
          logStageChange({
            leadId: dealId,
            description: `Stage changed from ${fromStage} to ${newStage} via pipeline table`,
            agent: lead.assigned || "Admin",
            timestamp: "Just now",
          });
        }

        // Update local state immediately
        setDynamicLeads((prev) =>
          prev.map((item) => (item.id === dealId ? { ...item, stage: newStage as any } : item))
        );

        setPipelineData((prev) => {
          const next: PipelineData = { ...prev };
          for (const [st, ids] of Object.entries(next)) {
            next[st] = ids.filter((id) => id !== dealId);
          }
          if (!next[newStage]) next[newStage] = [];
          next[newStage] = [...next[newStage], dealId];
          return next;
        });
      } catch (err: any) {
        showToast(err.message || "Failed to update deal stage", "error");
      }
    },
    [allDeals, showToast, logStageChange]
  );

  const handleOpenAddDeal = useCallback((stage?: string) => {
    setAddDealPreSelectedStage(stage);
    setAddDealModalOpen(true);
  }, []);

  const handleSaveDeal = useCallback(
    async (deal: PipelineDeal) => {
      try {
        await fetchApi(`/leads/${deal.leadId}`, {
          method: "PATCH",
          body: JSON.stringify({
            stage: deal.stage,
            priority: deal.priority,
            assigned: deal.assigned,
            value: deal.value,
            notes: deal.notes,
          }),
        });

        showToast(`Deal updated and placed in ${deal.stage}`, "success");
        await loadPipelineData();
      } catch (err: any) {
        showToast(err.message || "Failed to save deal", "error");
      }

      setAddDealModalOpen(false);
    },
    [showToast, loadPipelineData]
  );

  // ── Export handlers ─────────────────────────────────────────
  const handleExportPipelineCSV = () => {
    showToast("Preparing pipeline export...", "info");
    const rows = allDeals.map((l) => ({
      Company: l.company || "N/A",
      "Lead Name": l.name,
      Phone: l.phone || "",
      Email: l.email || "",
      Stage: l.stage,
      Value: l.value || "₹0",
      Priority: l.priority || "Medium",
      "Assigned To": l.assigned || "Unassigned",
      "Last Activity": l.lastActivity || "",
    }));

    setTimeout(() => {
      exportToCSV(rows, `pipeline-export-${formatExportDate()}.csv`);
      showToast("Download completed", "success");
    }, 400);
    setExportOpen(false);
  };

  const handleExportByStage = () => {
    showToast("Preparing export grouped by stage...", "info");
    const rows: Record<string, unknown>[] = [];
    stageConfig.forEach((cfg) => {
      const stageDeals = allDeals.filter((l) => l.stage === cfg.stage);
      rows.push({ "": `=== STAGE: ${cfg.label.toUpperCase()} (${stageDeals.length} deals) ===` });
      stageDeals.forEach((l) => {
        rows.push({
          Company: l.company || "N/A",
          "Lead Name": l.name,
          Stage: l.stage,
          Value: l.value || "₹0",
          Priority: l.priority || "Medium",
          "Assigned To": l.assigned || "Unassigned",
        });
      });
    });

    setTimeout(() => {
      exportToCSV(rows, `pipeline-by-stage-${formatExportDate()}.csv`);
      showToast("Download completed", "success");
    }, 400);
    setExportOpen(false);
  };

  const handleExportForecast = () => {
    showToast("Preparing forecast export...", "info");
    const totalPipelineSum = allDeals.reduce((sum, d) => {
      const num = parseFloat((d.value || "0").replace(/[₹,]/g, ""));
      return sum + (isNaN(num) ? 0 : num);
    }, 0);

    const rows = stageConfig.map((cfg) => {
      const deals = allDeals.filter((l) => l.stage === cfg.stage);
      const totalVal = deals.reduce((sum, d) => {
        const num = parseFloat((d.value || "0").replace(/[₹,]/g, ""));
        return sum + (isNaN(num) ? 0 : num);
      }, 0);

      return {
        Stage: cfg.label,
        "Deal Count": deals.length,
        "Total Value": `₹${totalVal.toLocaleString()}`,
        "% of Total Pipeline":
          totalPipelineSum > 0 ? `${Math.round((totalVal / totalPipelineSum) * 100)}%` : "0%",
      };
    });

    setTimeout(() => {
      exportToCSV(rows, `pipeline-forecast-${formatExportDate()}.csv`);
      showToast("Download completed", "success");
    }, 400);
    setExportOpen(false);
  };

  return (
    <CrmLayout title="Pipeline">
      <div className="p-5 md:p-6 space-y-5 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold text-gray-900">Deals Pipeline</h1>
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                {allDeals.length} Deals
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Visual pipeline tracking, dynamic stages, deal forecasting &amp; live movement
            </p>
          </div>

          <div className="flex items-center flex-wrap gap-2.5">
            {/* Project Filter */}
            {projectsList.length > 0 && (
              <select
                value={selectedProjectFilter}
                onChange={(e) => setSelectedProjectFilter(e.target.value)}
                className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm cursor-pointer"
              >
                <option value="all">🏢 All Projects</option>
                {projectsList.map((p) => (
                  <option key={p.id} value={p.id}>
                    🏢 {p.name}
                  </option>
                ))}
              </select>
            )}

            {/* View Switcher */}
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setViewMode("kanban")}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${
                  viewMode === "kanban" ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <i className="ri-layout-kanban-line" /> Kanban
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors border-l border-gray-200 cursor-pointer ${
                  viewMode === "table" ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <i className="ri-table-line" /> List
              </button>
              <button
                type="button"
                onClick={() => setViewMode("forecast")}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors border-l border-gray-200 cursor-pointer ${
                  viewMode === "forecast" ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <i className="ri-line-chart-line" /> Forecast
              </button>
            </div>

            {/* Export Dropdown */}
            <div className="relative" ref={exportRef}>
              <button
                type="button"
                onClick={() => setExportOpen(!exportOpen)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-xs font-medium transition-colors cursor-pointer shadow-sm whitespace-nowrap"
              >
                <i className="ri-download-line text-sm text-gray-500" /> Export
                <i className="ri-arrow-down-s-line text-xs opacity-70" />
              </button>
              {exportOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-200 shadow-xl w-56 py-1.5 z-50 animate-scale-in">
                  <button
                    type="button"
                    onClick={handleExportPipelineCSV}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer text-left"
                  >
                    <i className="ri-file-excel-2-line text-green-600 text-base" /> Export All Deals CSV
                  </button>
                  <button
                    type="button"
                    onClick={handleExportByStage}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer text-left"
                  >
                    <i className="ri-folder-zip-line text-indigo-600 text-base" /> Export Grouped by Stage
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    type="button"
                    onClick={handleExportForecast}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer text-left"
                  >
                    <i className="ri-line-chart-line text-amber-500 text-base" /> Export Forecast Analysis
                  </button>
                </div>
              )}
            </div>

            {/* Configure Stages Settings Link */}
            <button
              type="button"
              onClick={() => navigate("/settings/pipeline-config")}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shadow-sm"
              title="Configure Pipeline Stages in Settings"
            >
              <i className="ri-settings-3-line text-sm text-gray-500" /> Stages Config
            </button>

            {/* Add Deal Button */}
            <button
              type="button"
              onClick={() => handleOpenAddDeal()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-all whitespace-nowrap cursor-pointer shadow-sm hover:shadow"
            >
              <i className="ri-add-line text-base" /> Add Deal
            </button>
          </div>
        </div>

        {/* Dynamic Stats bar */}
        <PipelineStatsBar deals={allDeals} pipelineData={pipelineData} dealMetaMap={dealMetaMap} />

        {/* Loading state indicator */}
        {isLoading && (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <div className="inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mb-2" />
            <p className="text-xs text-gray-500">Loading pipeline &amp; deals data...</p>
          </div>
        )}

        {/* Views */}
        {!isLoading && viewMode === "kanban" && (
          <PipelineKanbanBoard
            deals={allDeals}
            pipelineData={filteredPipelineData}
            onPipelineDataChange={setPipelineData}
            dealMetaMap={dealMetaMap}
            onAddDeal={handleOpenAddDeal}
            stageConfig={stageConfig}
            stageDefinitions={stageDefinitions}
          />
        )}

        {!isLoading && viewMode === "table" && (
          <PipelineTableView
            deals={allDeals}
            stageConfig={stageConfig}
            onView={(deal) => navigate(`/leads/${deal.id}`)}
            onStageChange={handleTableStageChange}
            onAddDeal={handleOpenAddDeal}
          />
        )}

        {!isLoading && viewMode === "forecast" && (
          <PipelineForecastView deals={allDeals} stageConfig={stageConfig} />
        )}

        {/* Add Deal Modal */}
        {addDealModalOpen && (
          <AddDealModal
            preSelectedStage={addDealPreSelectedStage}
            existingDealLeadIds={existingDealLeadIds}
            onClose={() => setAddDealModalOpen(false)}
            onSave={handleSaveDeal}
          />
        )}
      </div>
    </CrmLayout>
  );
}